import {
  Button,
  Modal,
  TextAreaInput,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { useValidations } from '../../../../../../hooks/useValidations';
import useValidatorNotes from '../../../../../../hooks/useValidatorNotes';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { approveCourse, rejectCourse } from '../../../api';
import { HomologationSelectedCourse } from '../../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  course: HomologationSelectedCourse | null;
  requestId: number;
  onSuccess: () => void;
}

interface HomologationFormData {
  originCourseName: string;
  originCourseCode: string;
  originGrade: string;
  homologatedGrade: string;
  observation: string;
}

const MAX_OBSERVATION_LENGTH = 100;

export default function HomologationModal({
  isOpen,
  onClose,
  course,
  requestId,
  onSuccess,
}: Props) {
  const { t } = useTranslation(TranslationsKeys.HOMOLOGATION_DETAIL);
  const [loading, setLoading] = useState(false);
  const { msgValidations } = useValidations();
  const {
    config: gradeConfig,
    formattertNote,
    formattertExternalNote,
  } = useValidatorNotes();

  const requiredMsg = t('validations.required');

  const {
    control,
    handleSubmit,
    reset,
    getValues,
  } = useForm<HomologationFormData>({
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (isOpen && course) {
      reset({
        originCourseName: course.courseResult?.originCourseName ?? '',
        originCourseCode: course.courseResult?.originCourseCode ?? '',
        originGrade: course.courseResult?.originGrade?.toString() ?? '',
        homologatedGrade:
          course.courseResult?.homologatedGrade?.toString() ?? '',
        observation: course.observation ?? '',
      });
    }
  }, [isOpen, course, reset]);

  const handleApprove = useCallback(
    async (formData: HomologationFormData) => {
      if (!course) {
        return;
      }

      setLoading(true);
      const homologatedGrade = Math.min(
        gradeConfig.max,
        Math.max(gradeConfig.min, parseFloat(formData.homologatedGrade)),
      );
      const result = await approveCourse(requestId, course.semesterCourseId, {
        originCourseName: formData.originCourseName,
        originCourseCode: formData.originCourseCode,
        originGrade: parseFloat(formData.originGrade),
        homologatedGrade,
        observation: formData.observation || undefined,
      });
      setLoading(false);

      if (result.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: 'Error al aprobar la asignatura.',
        });
        return;
      }

      addToast({
        icon: 'success',
        color: 'success',
        text: 'Asignatura aprobada correctamente.',
      });
      onSuccess();
      onClose();
    },
    [course, requestId, gradeConfig, onSuccess, onClose],
  );

  const handleReject = useCallback(async () => {
    if (!course) {
      return;
    }

    const { observation } = getValues();

    setLoading(true);
    const result = await rejectCourse(requestId, course.semesterCourseId, {
      observation: observation || undefined,
    });
    setLoading(false);

    if (result.error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: 'Error al rechazar la asignatura.',
      });
      return;
    }

    addToast({
      icon: 'success',
      color: 'success',
      text: 'Asignatura rechazada.',
    });
    onSuccess();
    onClose();
  }, [course, requestId, getValues, onSuccess, onClose]);

  if (!course) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <form onSubmit={handleSubmit(handleApprove)}>
        <Row className="pb-4">
          <Col xs={12} className="text-center">
            <h1 className="text-dark fs-22">{t('modal.title')}</h1>
          </Col>
        </Row>

        {/* Origin course section */}
        <div
          className="mb-4 p-3"
          style={{
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#f5f9ff',
            borderRadius: 4,
          }}
        >
          <h6 className="fw-bold text-primary mb-3">
            {t('modal.originCourse')}
          </h6>
          <Row>
            <Col md={12}>
              <TextInput
                name="originCourseName"
                label={t('modal.courseName')}
                control={control}
                rules={{ required: requiredMsg }}
                placeholder="Ej: Cálculo I"
              />
            </Col>
            <Col md={6}>
              <TextInput
                name="originCourseCode"
                label={t('modal.code')}
                control={control}
                rules={{ required: requiredMsg }}
                placeholder="Ej: MAT101"
              />
            </Col>
            <Col md={6}>
              <TextInput
                name="originGrade"
                label={t('modal.grade')}
                control={control}
                rules={{
                  required: requiredMsg,
                  validate: (value: string) => {
                    if (!value) {
                      return false;
                    }
                    return (
                      formattertExternalNote(value) !== '' ||
                      String(t('modal.invalidGrade'))
                    );
                  },
                }}
                placeholder="Ej: 5.5"
              />
            </Col>
          </Row>
        </div>

        {/* Target course section */}
        <div
          className="mb-4 p-3"
          style={{
            borderLeft: '4px solid #00897b',
            backgroundColor: '#f0faf8',
            borderRadius: 4,
          }}
        >
          <h6 className="fw-bold text-primary">{t('modal.targetCourse')}</h6>
          <Row>
            <Col md={12}>
              <TextInput
                name="targetCourseName"
                label={t('modal.courseName')}
                value={course.courseName}
                disabled
              />
            </Col>
            <Col md={6}>
              <TextInput
                name="targetCourseCode"
                label={t('modal.code')}
                value={course.courseCode}
                disabled
              />
            </Col>
            <Col md={6}>
              <TextInput
                name="homologatedGrade"
                label={t('modal.homologatedGrade')}
                control={control}
                rules={{
                  required: requiredMsg,
                  validate: (value: string) => {
                    if (!value) {
                      return false;
                    }
                    return (
                      formattertNote(value) !== '' ||
                      String(t('modal.invalidGrade'))
                    );
                  },
                }}
                placeholder={`Ej: ${gradeConfig.min} - ${gradeConfig.max}`}
              />
            </Col>
          </Row>
        </div>

        {/* Observation */}
        <TextAreaInput
          name="observation"
          label={t('modal.observation')}
          control={control}
          placeholder={t('modal.observationPlaceholder')}
          showCharCounter
          maxLength={MAX_OBSERVATION_LENGTH}
          rules={{
            maxLength: msgValidations.invalidMaxLength(MAX_OBSERVATION_LENGTH),
          }}
        />

        {/* Footer buttons */}
        <Row className="pb-2 pt-4">
          <Col xs={12} sm={4}>
            <Button
              text={t('modal.cancel')}
              outlined
              fullwidth
              onClick={onClose}
              disabled={loading}
            />
          </Col>
          <Col xs={12} sm={4}>
            <Button
              text={t('modal.reject')}
              color="danger"
              fullwidth
              onClick={handleReject}
              loading={loading}
            />
          </Col>
          <Col xs={12} sm={4}>
            <Button
              text={t('modal.approve')}
              fullwidth
              type="submit"
              loading={loading}
            />
          </Col>
        </Row>
      </form>
    </Modal>
  );
}
